import React, { useState } from "react";

import { retrieveCustomerDetails, retrieveRelatedMeters } from "../../services/zoho.service";

import { useProposalDocument } from "./proposalDocumentsContext";

// Zoho customer record IDs are always exactly this many characters —
// checking this before hitting the CRM avoids a wasted round trip on an
// obviously-wrong ID
const ZOHO_ID_LENGTH = 18;

const ZohoCustomerValidator: React.FC = () => {
  const {
    payload,
    setPayload,
    zohoCustomerError,
    setZohoCustomerError,
    zohoIdValid,
    setZohoIdValid,
    relatedMeters,
    setRelatedMeters,
    metersFetched,
    setMetersFetched,
    setErrorMessage,
  } = useProposalDocument();
  const [isValidating, setIsValidating] = useState(false);
  const [isFetchingMeters, setIsFetchingMeters] = useState(false);

  const handleZohoIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPayload({ ...payload, zoho_id: value });
    // Clear validation state when user edits
    if (zohoCustomerError || zohoIdValid || metersFetched) {
      setZohoCustomerError("");
      setZohoIdValid(false);
      setMetersFetched(false);
      setRelatedMeters([]);
    }
  };

  const handleValidate = async () => {
    const trimmedId = payload.zoho_id.trim();
    if (!trimmedId) {
      setZohoCustomerError("Please enter a Zoho ID");
      setZohoIdValid(false);
      return;
    }

    if (trimmedId.length !== ZOHO_ID_LENGTH) {
      setZohoCustomerError(
        `Zoho Customer ID must be exactly ${ZOHO_ID_LENGTH.toString()} characters (got ${trimmedId.length.toString()}).`,
      );
      setZohoIdValid(false);
      return;
    }

    setIsValidating(true);
    setZohoCustomerError("");
    setZohoIdValid(false);
    setMetersFetched(false);
    setRelatedMeters([]);

    try {
      const result = await retrieveCustomerDetails(payload.zoho_id);

      if (!result.success) {
        setZohoIdValid(false);
        setZohoCustomerError(
          result.statusCode === 404
            ? "Customer not found. Please check the Zoho ID."
            : result.error || "Error validating customer ID",
        );
        return;
      }

      setZohoIdValid(true);
      setZohoCustomerError("");
      setErrorMessage("");

      // Customer's valid — now pull their meters in before the rest of
      // the form is revealed, so quote sections always have something
      // to work from
      setIsValidating(false);
      setIsFetchingMeters(true);

      const metersResult = await retrieveRelatedMeters(payload.zoho_id);
      if (metersResult.success) {
        setRelatedMeters(metersResult.data);
        setMetersFetched(true);
      } else {
        setZohoCustomerError(
          `Customer verified, but we couldn't retrieve their meters: ${metersResult.error || "Unknown error"}`,
        );
      }
    } catch (error) {
      setZohoIdValid(false);
      setZohoCustomerError("Error connecting to CRM. Please try again.");
    } finally {
      setIsValidating(false);
      setIsFetchingMeters(false);
    }
  };

  return (
    <div className="zoho-customer-validator">
      <div className="zoho-customer-validator__header">
        <h2>Zoho CRM Validation</h2>
        <p>Verify the customer exists in Zoho to create a new proposal</p>
      </div>

      <div className="zoho-customer-validator__input-group">
        <input
          type="text"
          className="zoho-customer-validator__input"
          placeholder="Enter Zoho Customer ID"
          value={payload.zoho_id}
          onChange={handleZohoIdChange}
          disabled={isValidating || isFetchingMeters}
          aria-label="Zoho customer ID"
          maxLength={18}
        />
        <button
          type="button"
          className="zoho-customer-validator__button"
          onClick={() => {
            void handleValidate();
          }}
          disabled={isValidating || isFetchingMeters || !payload.zoho_id.trim()}
          aria-label="Validate customer ID"
        >
          {isValidating || isFetchingMeters ? "Validating..." : "Validate"}
        </button>
      </div>

      {isValidating && (
        <div className="zoho-customer-validator__loading">
          <div className="spinner" aria-hidden="true" />
          <span>Checking customer...</span>
        </div>
      )}

      {isFetchingMeters && (
        <div className="zoho-customer-validator__loading">
          <div className="spinner" aria-hidden="true" />
          <span>Fetching meters...</span>
        </div>
      )}

      {zohoIdValid && metersFetched && (
        <div className="zoho-customer-validator__status zoho-customer-validator__status--valid">
          <span className="status-icon">✓</span>
          <span>
            Customer verified — {relatedMeters.length} meter
            {relatedMeters.length === 1 ? "" : "s"} found
          </span>
        </div>
      )}

      {zohoCustomerError && (
        <div className="zoho-customer-validator__status zoho-customer-validator__status--error">
          <span className="status-icon">!</span>
          <span>{zohoCustomerError}</span>
        </div>
      )}
    </div>
  );
};

export default ZohoCustomerValidator;
